import { Address } from '@klear/address'
import type { OrganizationSize } from '@klear/constants'
import type { Money } from '@klear/money'
import { COUNTRY_CODE_US, type CountryCode } from '@klear/constants/countries'
import type { OrganizationMeta } from '@klear/organization/meta'
import type { TraderJson, TraderMeta, TraderUserSnapshot } from '@klear/trader/meta'
import { Timestamp } from '@klear/time'
import cloneDeep from 'lodash/cloneDeep'

function metaFromInit(init: Partial<TraderJson>): TraderMeta {
  return {
    logoName: init.logoName ?? '',
    logoDownloadKey: init.logoDownloadKey ?? '',
    logoUrl: init.logoUrl ?? '',
    hasFx: init.hasFx ?? false,
    paymentAccountId: init.paymentAccountId ?? null
  }
}

export class Trader {
  static readonly __klearClass = 'Trader'

  id: number
  organizationId: number
  name: string
  description: string
  address: Address
  size?: OrganizationSize
  website?: string
  dunsNumber?: string
  lei?: string
  ein?: string
  vatNumber?: string
  parentOrganization?: string
  completedKyc?: boolean
  stripeAccountId?: string | null
  hasKlearAccount?: boolean
  organizationMeta?: OrganizationMeta

  private readonly meta: TraderMeta
  createdAt: Timestamp | null
  createdBy: TraderUserSnapshot | null
  updatedAt: Timestamp | null
  updatedBy: TraderUserSnapshot | null
  deletedAt: Timestamp | null
  deletedBy: TraderUserSnapshot | null
  klearAccountBalance?: Money | null
  klearFinancialAccountAdded?: boolean
  klearAccountAddedDate?: string
  hasUploadedCustomers?: boolean
  hasUploadedSuppliers?: boolean
  hasAddPO?: boolean
  hasFundedPO?: boolean
  hasOperationEvents?: boolean
  hasPaymentEvents?: boolean
  hasVerifiedExternalBankAccount?: boolean

  constructor(t?: Partial<TraderJson> | Trader) {
    if (t instanceof Trader) {
      this.id = t.id
      this.organizationId = t.organizationId
      this.name = t.name
      this.description = t.description
      this.address = new Address(t.address.toJSON())
      this.size = t.size
      this.website = t.website
      this.dunsNumber = t.dunsNumber
      this.lei = t.lei
      this.ein = t.ein
      this.vatNumber = t.vatNumber
      this.parentOrganization = t.parentOrganization
      this.completedKyc = t.completedKyc
      this.stripeAccountId = t.stripeAccountId
      this.hasKlearAccount = t.hasKlearAccount
      this.organizationMeta = t.organizationMeta ? cloneDeep(t.organizationMeta) : undefined
      this.meta = cloneDeep(t.meta)
      this.createdAt = t.createdAt
      this.createdBy = t.createdBy
      this.updatedAt = t.updatedAt
      this.updatedBy = t.updatedBy
      this.deletedAt = t.deletedAt
      this.deletedBy = t.deletedBy
      this.klearAccountBalance = t.klearAccountBalance
      this.klearFinancialAccountAdded = t.klearFinancialAccountAdded
      this.klearAccountAddedDate = t.klearAccountAddedDate
      this.hasUploadedCustomers = t.hasUploadedCustomers
      this.hasUploadedSuppliers = t.hasUploadedSuppliers
      this.hasAddPO = t.hasAddPO
      this.hasFundedPO = t.hasFundedPO
      this.hasOperationEvents = t.hasOperationEvents
      this.hasPaymentEvents = t.hasPaymentEvents
      this.hasVerifiedExternalBankAccount = t.hasVerifiedExternalBankAccount
      return
    }

    const init = t ?? {}
    this.id = init.id ?? 0
    this.organizationId = init.organizationId ?? 0
    this.name = init.name ?? ''
    this.description = init.description ?? ''
    this.address = new Address(init.address ?? { country: COUNTRY_CODE_US })
    this.size = init.size
    this.website = init.website
    this.dunsNumber = init.dunsNumber
    this.lei = init.lei
    this.ein = init.ein
    this.vatNumber = init.vatNumber
    this.parentOrganization = init.parentOrganization
    this.completedKyc = init.completedKyc
    this.stripeAccountId = init.stripeAccountId
    this.hasKlearAccount = init.hasKlearAccount
    this.organizationMeta = init.organizationMeta
    this.meta = metaFromInit(init)
    this.createdAt = init.createdAt ?? null
    this.createdBy = init.createdBy ?? null
    this.updatedAt = init.updatedAt ?? null
    this.updatedBy = init.updatedBy ?? null
    this.deletedAt = init.deletedAt ?? null
    this.deletedBy = init.deletedBy ?? null
    this.klearAccountBalance = init.klearAccountBalance
    this.klearFinancialAccountAdded = init.klearFinancialAccountAdded
    this.klearAccountAddedDate = init.klearAccountAddedDate
    this.hasUploadedCustomers = init.hasUploadedCustomers
    this.hasUploadedSuppliers = init.hasUploadedSuppliers
    this.hasAddPO = init.hasAddPO
    this.hasFundedPO = init.hasFundedPO
    this.hasOperationEvents = init.hasOperationEvents
    this.hasPaymentEvents = init.hasPaymentEvents
    this.hasVerifiedExternalBankAccount = init.hasVerifiedExternalBankAccount
  }

  get isSaved(): boolean {
    return this.id > 0
  }

  assertValid(): void {
    if (!this.name.trim()) {
      throw new Error('Trader name is required')
    }
  }

  get isKlear(): boolean {
    return this.organizationId === 1
  }

  get country(): CountryCode {
    return this.address.country
  }

  set country(value: CountryCode) {
    this.address = this.address.withCountry(value)
  }

  get logoName() {
    return this.meta.logoName
  }
  set logoName(logoName: string) {
    this.meta.logoName = logoName
  }

  get logoDownloadKey() {
    return this.meta.logoDownloadKey
  }
  set logoDownloadKey(logoDownloadKey: string) {
    this.meta.logoDownloadKey = logoDownloadKey
  }

  get logoUrl() {
    return this.meta.logoUrl
  }
  set logoUrl(logoUrl: string) {
    this.meta.logoUrl = logoUrl
  }

  get hasFx() {
    return this.meta.hasFx ?? false
  }
  set hasFx(value: boolean) {
    this.meta.hasFx = value
  }

  get paymentAccountId() {
    return this.meta.paymentAccountId ?? null
  }
  set paymentAccountId(value: number | null | undefined) {
    this.meta.paymentAccountId = value ?? null
  }

  metaForDb(): TraderMeta {
    return { ...this.meta }
  }

  toJSON(): TraderJson {
    return {
      id: this.id,
      organizationId: this.organizationId,
      name: this.name,
      description: this.description,
      address: this.address.toJSON(),
      size: this.size,
      website: this.website,
      dunsNumber: this.dunsNumber,
      lei: this.lei,
      ein: this.ein,
      vatNumber: this.vatNumber,
      parentOrganization: this.parentOrganization,
      completedKyc: this.completedKyc,
      stripeAccountId: this.stripeAccountId,
      hasKlearAccount: this.hasKlearAccount,
      organizationMeta: this.organizationMeta,
      logoName: this.logoName,
      logoDownloadKey: this.logoDownloadKey,
      logoUrl: this.logoUrl,
      hasFx: this.hasFx,
      paymentAccountId: this.paymentAccountId,
      createdAt: this.createdAt,
      createdBy: this.createdBy,
      updatedAt: this.updatedAt,
      updatedBy: this.updatedBy,
      deletedAt: this.deletedAt,
      deletedBy: this.deletedBy,
      klearAccountBalance: this.klearAccountBalance,
      klearFinancialAccountAdded: this.klearFinancialAccountAdded,
      klearAccountAddedDate: this.klearAccountAddedDate,
      hasUploadedCustomers: this.hasUploadedCustomers,
      hasUploadedSuppliers: this.hasUploadedSuppliers,
      hasAddPO: this.hasAddPO,
      hasFundedPO: this.hasFundedPO,
      hasOperationEvents: this.hasOperationEvents,
      hasPaymentEvents: this.hasPaymentEvents,
      hasVerifiedExternalBankAccount: this.hasVerifiedExternalBankAccount
    }
  }
}
